import {
  selectCustomerType,
  selectCustomerPhotoUri,
  selectCustomerFirstName
} from '../../../../../../store/selectors/customerProfile'
import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/browser'
import { useSelector } from 'react-redux'
import { useIntl, defineMessages } from 'react-intl'
import { MessageTopicKey } from '../../../../../../api/gqlaxy/gql/generated/graphql'
import { useSendListingMessage } from '../../../../../../hooks/listings/useSendListingMessage'
import { Avatar, Box, Button, ButtonGroup, Flex, Heading, Icon, Text } from '@homebotapp/hb-react-component-catalog'
import { selectSponsorshipFirstName, selectSponsorshipPhotoUri } from '../../../../../../store/selectors/sponsorship'
import ConfettiTarget from '../../../../../shared/Confetti/ConfettiTarget'
import { TrackingEventConstants } from '../../../../../../constants/tracking'
import { TrackingEventHelpers } from '../../../../../../helpers/tracking'
import { useTrackingContext } from '../../../../../../providers/tracking'
import { createBuyerMessage } from '../../../../../../api/mikasa/requests'
import { CUSTOMER_MESSAGES } from '../../../../../../constants/customerMessages'
import { GenericVerificationDirectMessage } from '../../../../GenericVerificationDirectMessage/GenericVerificationDirectMessage'
import { useVerificationFlowEnabled } from '../../../../../../hooks/useVerificationFlowEnabled'

export const MSG = defineMessages({
  head: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.head',
    defaultMessage: 'Want to talk numbers?'
  },
  prequal: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.prequal',
    defaultMessage: 'Get pre-qualified'
  },
  requestPrequal: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.requestPrequal',
    defaultMessage: 'Request pre-qualification'
  },
  confirm: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.confirm',
    defaultMessage: 'Confirm request'
  },
  cancel: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.cancel',
    defaultMessage: 'Cancel'
  },
  body: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.body',
    defaultMessage: 'Confirm what your monthly payment might look like for'
  },
  success: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.success',
    defaultMessage: 'Request sent!'
  },
  fail: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.fail',
    defaultMessage: 'Request failed. Try again?'
  },
  notifies: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.notifies',
    defaultMessage:
      'This notifies {firstName} that you are interested in getting pre-qualified. There is no obligation moving forward.'
  }
})

export interface EstimatedMonthlyPaymentBreakdownCustomerDMProps {
  address?: string
  buyerId?: string
  listingId?: string
}

enum DMStatus {
  Initial = 'initial',
  Confirmation = 'confirmation',
  Success = 'success',
  Fail = 'fail'
}

// this let's the confetti animation finish before resetting the state
const debounce = (func, delay) => {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      // @ts-ignore
      func.apply(this, args)
    }, delay)
  }
}

const animationTimer = 200

export const EstimatedMonthlyPaymentBreakdownCustomerDM = ({
  address,
  buyerId,
  listingId
}: EstimatedMonthlyPaymentBreakdownCustomerDMProps) => {
  const intl = useIntl()
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const sponsorPhoto = useSelector(selectSponsorshipPhotoUri)
  const customerFirstName = useSelector(selectCustomerFirstName)
  const sponsorFirstName = useSelector(selectSponsorshipFirstName)

  // Shared UI components
  const HeaderSection = () => (
    <>
      <Avatar src={sponsorPhoto ?? customerPhoto} mb={3} />
      <Heading mb={1} as='h4' size={['sm', 'md']} fontWeight='700'>
        {intl.formatMessage(MSG.head)}
      </Heading>
      {address && (
        <Text size={['xs', 'sm']} textAlign='center' mb={4} maxW={80}>
          {intl.formatMessage(MSG.body)}{' '}
          <Text size='inherit' as='span' fontWeight='semibold' color='neutral.900'>
            {address}
          </Text>
        </Text>
      )}
    </>
  )

  const FooterSection = () => (
    <Text size='2xs' textAlign='center' mt={4}>
      {intl.formatMessage(MSG.notifies, { firstName: sponsorFirstName ?? customerFirstName })}
    </Text>
  )

  return (
    <Flex mt={8} mx='auto' direction='column' align='center' maxW='25rem' w='100%'>
      <HeaderSection />

      {isVerificationFlowEnabled ? (
        <VerificationFlowExperience />
      ) : (
        <StandardExperience address={address} buyerId={buyerId} listingId={listingId} />
      )}

      <FooterSection />
    </Flex>
  )
}

// New verification flow experience
const VerificationFlowExperience = () => {
  const intl = useIntl()

  return (
    <GenericVerificationDirectMessage
      defaultTopicKey={CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topicKey}
      verifiedTopicKey={CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey}
      message={CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.message.defaultMessage}
      buttonText={intl.formatMessage(MSG.requestPrequal)}
      partnerType='LoanOfficer'
      flowType='finance'
      includePrequal={false}
      buttonProps={{
        leftIcon: <Icon name='message' />,
        colorScheme: 'brand',
        variant: 'solid',
        size: 'md',
        showAvatar: false,
        tracking: {
          guid: 'vwwBw2W6tcrTGacSTM2QqL',
          ui_context: 'Estimated Monthly Payment Breakdown Get Pre-qualified',
          descriptor: 'Get pre-qualified verification flow'
        }
      }}
    />
  )
}

// Standard animated button experience
interface StandardExperienceProps {
  address?: string
  buyerId?: string
  listingId?: string
}

const StandardExperience = ({ address, buyerId, listingId }: StandardExperienceProps) => {
  const intl = useIntl()
  const { trackingClient } = useTrackingContext()
  const customerType = useSelector(selectCustomerType)
  const [isLoading, setIsLoading] = useState(false)
  const [dmStatus, setDmStatus] = useState<DMStatus>(DMStatus.Initial)
  const [showMiniConfetti, setShowMiniConfetti] = useState(false)
  const [showMiniConfettiAlt, setShowMiniConfettiAlt] = useState(false)
  const [confettiCounter, setConfettiCounter] = useState(0)
  const { mutateAsync: sendListingMessage } = useSendListingMessage()

  const handleFinalStatus = () => {
    if (dmStatus === DMStatus.Fail) {
      setDmStatus(DMStatus.Initial)
    }

    if (dmStatus === DMStatus.Success) {
      setConfettiCounter(prevCounter => prevCounter + 1)
      const actionIndex = confettiCounter % 4

      switch (actionIndex) {
        case 0:
          setShowMiniConfetti(true)
          setShowMiniConfettiAlt(false)
          break
        case 1:
          setShowMiniConfettiAlt(true)
          break
        case 2:
          setShowMiniConfetti(true)
          break
        case 3:
          setShowMiniConfettiAlt(true)
          setTimeout(() => {
            if (confettiCounter % 4 === 3) {
              setShowMiniConfetti(false)
              setShowMiniConfettiAlt(false)
            }
          }, animationTimer)
          break
      }
    }
  }

  const debouncedHandleFinalStatus = debounce(handleFinalStatus, animationTimer)

  useEffect(() => {
    getTransformationStyles()
  }, [isLoading, dmStatus])

  const handleSubmit = async () => {
    setIsLoading(true)

    trackingClient?.clientRequestedInfo({
      request_type: TrackingEventConstants.CLIENT_REQUESTED_PREQUAL,
      customer_type: TrackingEventHelpers.getTrackingCustomerType(customerType),
      ui_context: 'Estimated Monthly Payment Breakdown Get Pre-qualified'
    })

    try {
      if (listingId) {
        await sendListingMessage({
          input: {
            listingId: listingId,
            topicKey: MessageTopicKey.ListingsPrequalRequest,
            message: 'I want to get pre-qualified for this home.'
          }
        })
      } else if (buyerId) {
        await createBuyerMessage(
          buyerId,
          {
            metadata: null,
            topic: CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topic,
            topicKey: CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topicKey,
            message: CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.message.defaultMessage
          },
          trackingClient
        )
      }
      setDmStatus(DMStatus.Success)
    } catch (err: any) {
      setDmStatus(DMStatus.Fail)
      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: err.message || '',
          requestData: err.config?.data || {},
          requestErrors: Object.values(err.response?.data?.errors) || []
        }
      })
    } finally {
      setTimeout(() => {
        setIsLoading(false)
      }, 300)
    }
  }

  const getTransformationStyles = (): React.CSSProperties => {
    let transformation = 'translateY(0%)'

    switch (dmStatus) {
      case DMStatus.Initial:
        break
      case DMStatus.Confirmation:
        transformation = 'translateY(-37%)'
        break
      case DMStatus.Fail:
        transformation = 'translateY(-73.5%)'
        break
      case DMStatus.Success:
        transformation = 'translateY(-73.5%)'
        break
    }

    return {
      transform: transformation
    }
  }

  return (
    <>
      {/* @ts-ignore */}
      <ConfettiTarget go={dmStatus === DMStatus.Success} numberOfPieces={40}>
        {/* @ts-ignore */}
        <ConfettiTarget go={showMiniConfetti} numberOfPieces={32}>
          {/* @ts-ignore */}
          <ConfettiTarget go={showMiniConfettiAlt} numberOfPieces={72}>
            <Box position='relative' height={12} mb={0} overflow='hidden' w={['80%', '100%']} mx='auto'>
              <Flex
                direction='column'
                gap={4}
                style={getTransformationStyles()}
                transition={'.15s ease-in-out transform'}
              >
                <Flex direction='column' gap={4}>
                  <Box transition={'.15s ease-in-out transform'}>
                    <Button
                      width='100%'
                      variant={'solid'}
                      opacity={'100% !important'}
                      colorScheme='brand'
                      disabled={dmStatus !== DMStatus.Initial}
                      onClick={() => setDmStatus(DMStatus.Confirmation)}
                    >
                      {intl.formatMessage(MSG.prequal)}
                    </Button>
                  </Box>
                  <ButtonGroup
                    width='100%'
                    transition={'1s ease-in-out opacity'}
                    opacity={dmStatus === DMStatus.Fail ? 0 : '100%'}
                  >
                    <Box overflow='hidden' width={isLoading ? 0 : '50%'} transition={'.1s ease-in-out all'}>
                      <Button
                        width={isLoading ? 0 : '100%'}
                        transition={'.2s ease-in-out all'}
                        px={6}
                        onClick={() => setDmStatus(DMStatus.Initial)}
                        disabled={isLoading || dmStatus !== DMStatus.Confirmation}
                      >
                        {intl.formatMessage(MSG.cancel)}
                      </Button>
                    </Box>
                    <Button
                      w='100%'
                      disabled={dmStatus !== DMStatus.Confirmation}
                      isLoading={isLoading}
                      opacity={'100% !important'}
                      onClick={() => handleSubmit()}
                      colorScheme='brand'
                    >
                      {intl.formatMessage(MSG.confirm)}
                    </Button>
                  </ButtonGroup>
                </Flex>
                <Flex>
                  <Button
                    w='100%'
                    variant='outline'
                    disabled={dmStatus === DMStatus.Confirmation || dmStatus === DMStatus.Initial}
                    leftIcon={
                      dmStatus === DMStatus.Success ? <Icon name='check-circle-fa' width={3} height={3} /> : undefined
                    }
                    onClick={() => debouncedHandleFinalStatus()}
                  >
                    {dmStatus === DMStatus.Success && intl.formatMessage(MSG.success)}
                    {dmStatus === DMStatus.Fail && intl.formatMessage(MSG.fail)}
                  </Button>
                </Flex>
              </Flex>
            </Box>
          </ConfettiTarget>
        </ConfettiTarget>
      </ConfettiTarget>
      <Text
        style={{ transform: dmStatus === DMStatus.Fail ? 'translateY(0%)' : 'translateY(-100%)' }}
        zIndex={dmStatus === DMStatus.Fail ? 1 : -1}
        opacity={dmStatus === DMStatus.Fail ? '100%' : 0}
        mb={dmStatus === DMStatus.Fail ? 2 : -2}
        mt={dmStatus === DMStatus.Fail ? 0 : -2}
        transition='.2s ease-in-out all'
        as='span'
        size='sm'
        fontWeight='semibold'
        color='warning.700'
      >
        We failed to send the message
      </Text>
    </>
  )
}
